import { motion } from 'framer-motion'
import { FileText, Receipt, CalendarCheck, ThumbsUp, ChevronRight } from 'lucide-react'

const steps = [
  {
    number: '01',
    icon: FileText,
    title: 'Você informa o serviço',
    description: 'Conte o que precisa e envie os detalhes do serviço.',
  },
  {
    number: '02',
    icon: Receipt,
    title: 'Recebe o orçamento',
    description: 'Enviamos um orçamento claro, justo e sem compromisso.',
  },
  {
    number: '03',
    icon: CalendarCheck,
    title: 'Agende o atendimento',
    description: 'Combinamos o melhor dia e horário para realizar o serviço.',
  },
  {
    number: '04',
    icon: ThumbsUp,
    title: 'Serviço realizado',
    description: 'Executamos o serviço com qualidade e cuidado.',
  },
]

const HowItWorksSection = () => {
  return (
    <section
      id="como-funciona"
      data-testid="how-it-works-section"
      className="py-24 md:py-32 bg-white"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        {/* Header */}
        <div className="mb-16 lg:mb-20">
          <motion.div
            className="flex items-center gap-4 mb-5"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
          >
            <div className="h-px w-10 bg-[#F2B300]" />
            <span className="text-[#F2B300] text-[11px] font-bold uppercase tracking-[0.2em]">
              Processo
            </span>
          </motion.div>
          <motion.h2
            className="text-4xl sm:text-5xl font-bold text-[#062B63] font-outfit tracking-tight"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1, duration: 0.6 }}
          >
            Como funciona
          </motion.h2>
        </div>

        {/* Steps grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10 lg:gap-6 relative">
          {/* Connector line (desktop) */}
          <motion.div
            className="hidden lg:block absolute top-6 left-[12.5%] right-[12.5%] h-px bg-[#062B63]/10"
            initial={{ scaleX: 0 }}
            whileInView={{ scaleX: 1 }}
            viewport={{ once: true }}
            style={{ transformOrigin: 'left' }}
            transition={{ delay: 0.5, duration: 1 }}
          />

          {steps.map((step, i) => {
            const Icon = step.icon
            return (
              <motion.div
                key={step.number}
                data-testid={`step-${step.number}`}
                className="relative"
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-60px' }}
                transition={{ duration: 0.6, delay: i * 0.12 }}
              >
                {/* Large number watermark */}
                <div
                  className="absolute -top-4 -left-2 text-[6rem] font-bold text-[#062B63] font-outfit select-none pointer-events-none leading-none"
                  style={{ opacity: 0.04 }}
                >
                  {step.number}
                </div>

                {/* Step number circle */}
                <div className="relative w-12 h-12 bg-[#F2B300] flex items-center justify-center mb-6 z-10">
                  <span className="text-[#062B63] font-bold text-sm font-outfit">{step.number}</span>
                </div>

                {/* Icon */}
                <div className="w-10 h-10 flex items-center justify-center mb-4">
                  <Icon size={24} className="text-[#062B63]" />
                </div>

                {/* Content */}
                <h3 className="text-[#062B63] font-bold text-base mb-2.5 font-outfit leading-snug">
                  {step.title}
                </h3>
                <p className="text-[#062B63]/55 text-sm leading-relaxed">{step.description}</p>

                {/* Arrow connector between steps (desktop) */}
                {i < steps.length - 1 && (
                  <div className="hidden lg:flex absolute top-5 -right-4 z-20">
                    <ChevronRight size={18} className="text-[#F2B300]" />
                  </div>
                )}
              </motion.div>
            )
          })}
        </div>
      </div>
    </section>
  )
}

export default HowItWorksSection
